import axios from 'axios';
import { axiosConfig } from 'config';
import * as actions from 'store/actions';
import store from 'store';

// O WordPress em dev pode emitir warnings PHP (ex.: deprecações de plugins)
// antes do JSON, o que quebraria o parse do axios. Sanitiza a resposta
// removendo qualquer conteúdo não-JSON que preceda o payload.
const sanitizeWPResponse = (data) => {
    if (typeof data !== 'string') return data;
    const start = data.search(/[[{]/);
    if (start > 0) data = data.slice(start);
    try {
        return JSON.parse(data);
    } catch (e) {
        return data;
    }
};

const api = axios.create({ ...axiosConfig, transformResponse: [sanitizeWPResponse] });

// GET público de sta/v1 via ?rest_route= — mantém o namespace fora do PATH,
// contornando behaviors do CloudFront que bloqueiam /cms/index.php/wp-json/sta/v1/*.
// O gateway do CMS libera leitura (GET/HEAD) sem assinatura.
export const wpRestRoute = ( path ) => `/?rest_route=${ encodeURIComponent( path ) }`;

// --- Modo direto React <-> WordPress ---
// Escritas via ?rest_route= com X-HTTP-Method-Override: a requisição sai como
// GET (método permitido pelo behavior wp-json do CloudFront, sem body — CF
// rejeita GET com corpo) e o WP REST Server executa como POST. Os campos do
// payload trafegam na query string; o WP os mescla via get_param().
// Mesma origem => sem preflight/CORS. Validado em produção (200 + persistência).
export const buildWpRestUrl = ( routePath, query ) => {
    const [ cleanPath, existingQuery ] = routePath.split( '?' );
    let url = `/?rest_route=${ encodeURIComponent( cleanPath ) }`;
    const extra = [ existingQuery, query, `_${Date.now()}` ].filter( Boolean ).join( '&' );
    if ( extra ) {
        url += `&${extra}`;
    }
    return url;
};

const serializeToQuery = ( payload, parentKey = '' ) => {
    const params = [];
    const isPlainObject = ( v ) => v !== null && typeof v === 'object' && !Array.isArray( v );

    const add = ( key, value ) => {
        if ( value === undefined || value === null ) return;
        params.push( [ key, String( value ) ] );
    };

    const build = ( value, keyPrefix ) => {
        if ( Array.isArray( value ) ) {
            value.forEach( ( item, idx ) => {
                if ( isPlainObject( item ) || Array.isArray( item ) ) {
                    const nextKey = keyPrefix ? `${keyPrefix}[${idx}]` : `${idx}`;
                    build( item, nextKey );
                } else {
                    // array primitivo: usa chave[] para PHP receber como array
                    const nextKey = keyPrefix ? `${keyPrefix}[]` : `${idx}`;
                    add( nextKey, item );
                }
            });
        } else if ( isPlainObject( value ) ) {
            Object.entries( value ).forEach( ( [ k, v ] ) => {
                const nextKey = keyPrefix ? `${keyPrefix}[${k}]` : k;
                if ( Array.isArray( v ) || isPlainObject( v ) ) {
                    build( v, nextKey );
                } else {
                    add( nextKey, v );
                }
            } );
        } else if ( keyPrefix ) {
            add( keyPrefix, value );
        }
    };

    if ( Array.isArray( payload ) ) {
        // payload raiz é array (postPage): serializa como 0[PageNumber]=1&0[SimulationID]=...
        build( payload, '' );
    } else {
        build( payload, '' );
    }

    return new URLSearchParams( params ).toString();
};

export const wpDirectPost = async ( routePath, payload = {}, query = '', config ) => {
    const qs = [ serializeToQuery( payload ), query ].filter( Boolean ).join( '&' );
    const url = buildWpRestUrl( routePath, qs );

    const doCall = ( instance, u ) => instance.get( u, {
        headers: { 'X-HTTP-Method-Override': 'POST' },
        ...config,
    });

    try {
        return await doCall( api, url );
    } catch ( error ) {
        const status = error && error.response ? error.response.status : 0;
        if ( status >= 400 && status < 500 && status !== 429 ) {
            throw error;
        }
        let lastError = error;
        for ( const base of CMS_FALLBACK_BASES ) {
            const instance = axios.create({
                ...axiosConfig,
                baseURL: `${base}/cms/index.php/wp-json`,
                transformResponse: [sanitizeWPResponse],
                timeout: 8000,
            });
            try {
                return await doCall( instance, url.replace( /^\//, '' ) );
            } catch ( e ) {
                lastError = e;
                const s = e && e.response ? e.response.status : 0;
                if ( s >= 400 && s < 500 && s !== 429 ) {
                    throw e;
                }
            }
        }
        throw lastError;
    }
};

// Fallback da API do CMS: mesma rota /cms/index.php/wp-json/, trocando apenas
// a base. Ordem: origem atual (CloudFront/domínio) → domínio absoluto → LB.
// Observação: o certificado do ALB cobre apenas *.santillana.com.br, então o
// HTTPS do DNS bruto do LB falha validação no navegador; o http:// do LB
// funciona quando a página também é http (ex.: local) ou após o ALB passar a
// servir TLS válido para esse hostname.
export const CMS_FALLBACK_BASES = [
    process.env.REACT_APP_CMS_FALLBACK_BASE,
    'https://www.santillana.com.br',
    'http://sta-lb-portalweb-dev-467455392.us-east-1.elb.amazonaws.com',
].filter( Boolean );

const fallbackApis = CMS_FALLBACK_BASES.map( ( base ) => axios.create({
    ...axiosConfig,
    baseURL: `${base}/cms/index.php/wp-json`,
    transformResponse: [sanitizeWPResponse],
    timeout: 8000,
}) );

// GET resiliente: tenta a origem atual e, em falha de gateway/rede (5xx ou sem
// resposta), percorre as bases de fallback em ordem. Erros 4xx são respostas
// reais da API e NÃO geram fallback (exceto 429, que indica bloqueio).
export const apiGetWithFallback = async ( path, config ) => {
    try {
        return await api.get( path, config );
    } catch ( error ) {
        const status = error && error.response ? error.response.status : 0;
        if ( status >= 400 && status < 500 && status !== 429 ) {
            throw error;
        }
        let lastError = error;
        for ( const instance of fallbackApis ) {
            try {
                return await instance.get( path, config );
            } catch ( e ) {
                lastError = e;
                const s = e && e.response ? e.response.status : 0;
                if ( s >= 400 && s < 500 && s !== 429 ) {
                    throw e;
                }
            }
        }
        throw lastError;
    }
};

// Escrita resiliente: mesmo contrato do apiGetWithFallback, porém sempre
// passando por um portal que assina o HMAC — nunca direto ao CMS. Tenta a
// combinação de prefixos aceitos pelo portal (/simulator/api, /sta-api) na
// origem atual e, em 5xx/rede, nas bases alternativas (domínio/LB).
const SIM_WRITE_PREFIXES = [ '/simulator/api', '/sta-api' ];

// Remove prefixos de escrita já presentes no path (protege contra dupla
// concatenação tipo /simulator/api/simulator/api/...).
const stripWritePrefixes = ( path ) => {
    let out = path;
    for ( const prefix of SIM_WRITE_PREFIXES ) {
        while ( out.startsWith( prefix ) ) {
            out = out.slice( prefix.length );
        }
    }
    return out;
};

// POST com corpo real via admin-post.php (fora do behavior wp-json do
// CloudFront, que bloqueia POST). Usado pelo cadastro do simulador.
// Usa axios direto (sem baseURL) para evitar que o path seja concatenado
// com o baseURL do WordPress REST (/cms/index.php/wp-json).
export const wpAdminPost = async ( action, payload, config ) => {
    const path = `/cms/wp-admin/admin-post.php?action=${action}`;
    const doCall = ( base, u ) => axios.post( `${base}${u}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        ...config,
    });

    try {
        return await doCall( '', path );
    } catch ( error ) {
        const status = error && error.response ? error.response.status : 0;
        if ( status >= 400 && status < 500 && status !== 429 ) {
            throw error;
        }
        let lastError = error;
        for ( const base of CMS_FALLBACK_BASES ) {
            try {
                return await doCall( base, path );
            } catch ( e ) {
                lastError = e;
                const st = e && e.response ? e.response.status : 0;
                if ( st >= 400 && st < 500 && st !== 429 ) {
                    throw e;
                }
            }
        }
        throw lastError;
    }
};

export const apiPostWithFallback = async ( rawPath, data, config ) => {
    const path = stripWritePrefixes( rawPath );
    let lastError = null;

    const attempt = async ( url ) => {
        try {
            return await simApiProxy.post( url, data, config );
        } catch ( error ) {
            const status = error && error.response ? error.response.status : 0;
            if ( status >= 400 && status < 500 && status !== 429 ) {
                throw error;
            }
            throw { gated: true, error };
        }
    };

    // 1) Origem atual, ambos os prefixos.
    for ( const prefix of SIM_WRITE_PREFIXES ) {
        try {
            return await attempt( `${prefix}${path}` );
        } catch ( e ) {
            if ( !e || !e.gated ) throw e && e.error ? e.error : e;
            lastError = e.error;
        }
    }

    // 2) Bases alternativas (domínio absoluto / LB), ambos os prefixos.
    for ( const base of CMS_FALLBACK_BASES ) {
        for ( const prefix of SIM_WRITE_PREFIXES ) {
            try {
                return await axios({
                    ...axiosConfig,
                    method: 'post',
                    url: `${base}${prefix}${path}`,
                    data,
                    transformResponse: [sanitizeWPResponse],
                    timeout: 8000,
                    ...config,
                });
            } catch ( error ) {
                const status = error && error.response ? error.response.status : 0;
                if ( status >= 400 && status < 500 && status !== 429 ) {
                    throw error;
                }
                lastError = error;
            }
        }
    }

    throw lastError;
};

// Leitura (GET) do simulador: vai pelo path wp-json, que o proxy do portal
// (proxyCmsApi) assina com HMAC e encaminha ao CMS.
const simApi = axios.create({ ...axiosConfig, baseURL: '/cms/index.php/wp-json', transformResponse: [sanitizeWPResponse] });

// Escrita (POST/PUT) do simulador: vai por /simulator/api, cujo behavior
// padrão do CloudFront (origem do portal) permite POST. O portal assina o
// HMAC e encaminha ao CMS — evita o bloqueio de POST no behavior wp-json.
const simApiProxy = axios.create({ ...axiosConfig, baseURL: '/simulator/api', transformResponse: [sanitizeWPResponse] });

const attachInterceptors = (instance) => {
    instance.interceptors.request.use(config => {
        store.dispatch(actions.loadingShow(`${config.method}:${config.url}`))
        return config;
    });

    instance.interceptors.response.use(response => {
        store.dispatch(actions.loadingHide(`${response.config.method}:${response.config.url}`))

        return response;
    }, error => {
        store.dispatch(actions.loadingHide("force-hide"))

        if (!error.response) {
            error = error.toJSON();
        }

        let alertProps = {
            color: 'danger',
            title: error.name,
            message: error.message
        }

        store.dispatch(actions.alertShow(alertProps))
        return Promise.reject(error);
    });
};

attachInterceptors(api);
attachInterceptors(simApi);
attachInterceptors(simApiProxy);

export default api;
export { simApi, simApiProxy };